import React from 'react';
import { convertToKRW } from '../../lib/utils';
import type { CodeValueType } from '../../types/types';
import {
    AllCoachesSalaryRow,
    AllCoachesSalaryTotals,
    failureReasonText,
    isFoldableZeroRow,
    rowBadges,
    SalaryRowBadge,
} from './allCoachesSalary';
import { SALARY_BADGE_LABEL } from './exportAllCoachesSalaryExcel';

const BADGE_STYLE: Record<SalaryRowBadge, string> = {
    retired: 'bg-[#EEF0F3] text-[#4B5563]',
    saved: 'bg-[#E6F8F0] text-[#067A46]',
    unsaved: 'bg-[#F2F4F6] text-[#6B7280]',
    bracketMissing: 'bg-[#FFF6E5] text-[#8A5A00]',
    integrity: 'bg-[#FFF6E5] text-[#8A5A00]',
    failed: 'bg-[#FDECEA] text-[#C0392B]',
};

const won = (value: number) => `${convertToKRW(value)}원`;

type Props = {
    rows: AllCoachesSalaryRow[];
    totals: AllCoachesSalaryTotals;
    loadedCount: number;
    total: number;
    isLoading: boolean;
    onSelectCoach: (coach: CodeValueType) => void;
    onReload: () => void;
    onExcel: () => void;
};

const NumberCell = ({ value, strong }: { value: number | null; strong?: boolean }) => (
    <td
        className={`px-[12px] py-[12px] text-right whitespace-nowrap tabular-nums ${
            strong ? 'font-noto-medium text-[#1E5BD8]' : 'text-[#3D4244]'
        }`}
    >
        {value === null ? <span className="text-[#B0B5BC]">-</span> : won(value)}
    </td>
);

const AllCoachesSalaryTable = ({ rows, totals, loadedCount, total, isLoading, onSelectCoach, onReload, onExcel }: Props) => {
    const [showZeroRows, setShowZeroRows] = React.useState(false);
    const zeroRows = rows.filter(isFoldableZeroRow);
    const visibleRows = showZeroRows ? rows : rows.filter((row) => !isFoldableZeroRow(row));

    if (total === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-[80px] text-[16px] text-[#6B7280]">
                급여를 계산할 강사가 없어요.
            </div>
        );
    }

    return (
        <div className="flex flex-col w-full" aria-label="전체 강사 급여">
            <div className="flex items-center justify-between mb-[12px] gap-[12px]">
                <p className="text-[14px] leading-[20px] text-[#6B7280] break-keep">
                    급여 계산 강사 목록 기준이에요. 강사 이름을 누르면 그 강사 급여 화면으로 가요.
                </p>
                <div className="flex items-center gap-[8px] flex-shrink-0">
                    <button
                        type="button"
                        onClick={onReload}
                        disabled={isLoading}
                        className="h-[33px] px-[14px] rounded-[7px] bg-[#F2F4F6] text-[14px] text-[#3D4244] disabled:opacity-50"
                    >
                        다시 불러오기
                    </button>
                    <button
                        type="button"
                        onClick={onExcel}
                        disabled={isLoading}
                        className="h-[33px] px-[14px] rounded-[7px] bg-[#F2F4F6] text-[14px] text-[#3D4244] disabled:opacity-50"
                    >
                        엑셀 받기
                    </button>
                </div>
            </div>

            {isLoading && (
                <div role="status" className="mb-[12px] text-[14px] text-[#1E5BD8]">
                    {`급여를 불러오고 있어요 (${loadedCount}/${total}명)`}
                </div>
            )}

            <div className="w-full overflow-x-auto rounded-[12px] border border-solid border-[#E4E7EB]">
                <table className="w-full text-[15px] border-collapse">
                    <thead>
                        <tr className="bg-[#F6F8FA] text-[#6B7280] text-[14px]">
                            <th className="px-[12px] py-[12px] text-left font-normal">강사</th>
                            <th className="px-[12px] py-[12px] text-right font-normal">매출</th>
                            <th className="px-[12px] py-[12px] text-right font-normal">수업료</th>
                            <th className="px-[12px] py-[12px] text-right font-normal">인센티브</th>
                            <th className="px-[12px] py-[12px] text-right font-normal">기본급</th>
                            <th className="px-[12px] py-[12px] text-right font-normal">기타 수당</th>
                            <th className="px-[12px] py-[12px] text-right font-normal">3.3% 공제</th>
                            <th className="px-[12px] py-[12px] text-right font-normal">최종 급여</th>
                            <th className="px-[12px] py-[12px] text-left font-normal">상태</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRows.map((row) => {
                            const ok = row.state === 'ok' ? row : null;
                            return (
                                <tr key={row.coach.code} className="border-t border-solid border-[#EEF0F2]">
                                    <td className="px-[12px] py-[12px] text-left whitespace-nowrap">
                                        <button
                                            type="button"
                                            onClick={() => onSelectCoach(row.coach)}
                                            className="text-[#3D4244] underline-offset-2 hover:underline hover:text-[#1E5BD8]"
                                        >
                                            {row.coach.value}
                                        </button>
                                    </td>
                                    <NumberCell value={ok ? ok.calc.settlement : null} />
                                    <NumberCell value={ok ? ok.calc.classFee : null} />
                                    <NumberCell value={ok ? ok.calc.incentiveKRW : null} />
                                    <NumberCell value={ok ? ok.calc.baseSalary : null} />
                                    <NumberCell value={ok ? ok.calc.etcBonusKRW : null} />
                                    <NumberCell value={ok ? -ok.calc.deductionApplied || 0 : null} />
                                    <NumberCell value={ok ? ok.calc.finalSalary : null} strong />
                                    <td className="px-[12px] py-[12px] text-left">
                                        {row.state === 'pending' ? (
                                            <span className="text-[13px] text-[#9AA0A6]">불러오는 중</span>
                                        ) : (
                                            <div className="flex flex-wrap gap-[4px]">
                                                {rowBadges(row).map((badge) => (
                                                    <span
                                                        key={badge}
                                                        title={
                                                            row.state === 'failed' ? failureReasonText(row.reason) : undefined
                                                        }
                                                        className={`rounded-full px-[8px] py-[2px] text-[13px] whitespace-nowrap ${BADGE_STYLE[badge]}`}
                                                    >
                                                        {SALARY_BADGE_LABEL[badge]}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {zeroRows.length > 0 && (
                            <tr className="border-t border-solid border-[#EEF0F2]">
                                <td colSpan={9} className="px-[12px] py-[10px] text-center">
                                    <button
                                        type="button"
                                        onClick={() => setShowZeroRows((v) => !v)}
                                        className="text-[14px] text-[#6B7280] hover:text-[#1E5BD8]"
                                    >
                                        {showZeroRows ? '0원 강사 접기' : `0원 강사 ${zeroRows.length}명 더 보기`}
                                    </button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="border-t-2 border-solid border-[#D9DDE6] bg-[#F4F7FF]" aria-label="합계">
                            <td className="px-[12px] py-[14px] text-left whitespace-nowrap font-noto-medium text-[#3D4244]">
                                {`합계 (${totals.includedCount}명)`}
                            </td>
                            <NumberCell value={totals.settlement} />
                            <NumberCell value={totals.classFee} />
                            <NumberCell value={totals.incentiveKRW} />
                            <NumberCell value={totals.baseSalary} />
                            <NumberCell value={totals.etcBonusKRW} />
                            <NumberCell value={-totals.deductionApplied || 0} />
                            <NumberCell value={totals.finalSalary} strong />
                            <td className="px-[12px] py-[14px] text-left">
                                <div className="flex flex-col gap-[2px] text-[13px]">
                                    {totals.pendingCount > 0 && (
                                        <span className="text-[#6B7280]">{`${totals.pendingCount}명 아직 불러오는 중`}</span>
                                    )}
                                    {totals.failedCount > 0 && (
                                        <span className="text-[#C0392B]">{`불러오지 못함 ${totals.failedCount}명 제외`}</span>
                                    )}
                                    {totals.integrityCount > 0 && (
                                        <span className="text-[#8A5A00]">{`일부 수업 제외 ${totals.integrityCount}명 포함`}</span>
                                    )}
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default AllCoachesSalaryTable;
